import React, { useState, useEffect } from 'react';
import { List, Button, Typography, Space, Spin, message, Upload, Modal } from 'antd';
import { DownloadOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { getToken } from '../tokens';

const { Text, Link } = Typography;

interface FileInfo {
  id: string;
  filename_download: string;
  title: string;
  type: string;
  filesize: number | string; // Can be string from API
  uploaded_on: string;
}

interface MultiFileUploadProps {
  value?: string; // JSON string of file IDs
  onChange?: (value: string) => void;
  disabled?: boolean;
  onAutoSave?: () => void; // Function to trigger auto-save
}

const DIRECTUS_BASE_URL = 'https://melcarnedirectus2.digipulse.ch';

// Component for displaying file count in table views
export const MultiFileUploadDisplay: React.FC<{ value?: string }> = ({ value }) => {
  const fileIds = React.useMemo(() => {
    if (!value) return [];
    try {
      return JSON.parse(value);
    } catch {
      return [];
    }
  }, [value]);

  if (fileIds.length === 0) {
    return <Text type="secondary">Aucun fichier</Text>;
  }

  return (
    <Text>
      {fileIds.length} fichier{fileIds.length > 1 ? 's' : ''}
    </Text>
  );
};

const MultiFileUpload: React.FC<MultiFileUploadProps> = ({
  value,
  onChange,
  disabled = false,
  onAutoSave
}) => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  
  // Parse file IDs from JSON string or array
  const fileIds = React.useMemo(() => {
    console.log("MultiFileUpload value type:", typeof(value));
    console.log("MultiFileUpload value:", value);
    
    if (!value) return [];
    
    // If it's already an array, return it
    if (Array.isArray(value)) {
      console.log("Value is already an array:", value);
      return value;
    }
    
    // If it's a string, try to parse it as JSON
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        console.log("Parsed JSON:", parsed);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.log("JSON parse error:", e);
        return [];
      }
    }
    
    return [];
  }, [value]);

  console.log("Final fileIds:", fileIds);

  // Fetch file information for each file ID
  useEffect(() => {
    const fetchFiles = async () => {
      if (fileIds.length === 0) {
        setFiles([]);
        return;
      }

      setLoading(true);
      try {
        const filePromises = fileIds.map(async (fileId: string) => {
          const { token } = getToken();
          const response = await fetch(`${DIRECTUS_BASE_URL}/files/${fileId}`, {
            cache: 'no-store',
            headers: {
              'Authorization': `Bearer ${token || ''}`
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            return result.data;
          }
          return null;
        });

        const fileResults = await Promise.all(filePromises);
        const validFiles = fileResults.filter(file => file !== null);
        console.log("Fetched files with sizes:", validFiles.map(f => ({ id: f.id, name: f.filename_download, size: f.filesize, sizeType: typeof f.filesize })));
        setFiles(validFiles);
      } catch (error) {
        console.error('Error fetching files:', error);
        messageApi.error('Erreur lors du chargement des fichiers');
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [fileIds, messageApi]);

  // Handle file download
  const handleDownload = (file: FileInfo) => {
    const downloadUrl = `${DIRECTUS_BASE_URL}/assets/${file.id}?download`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = file.filename_download || file.title || `file-${file.id}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file preview/view
  const handleView = (file: FileInfo) => {
    const viewUrl = `${DIRECTUS_BASE_URL}/assets/${file.id}`;
    window.open(viewUrl, '_blank');
  };

  // Handle file removal
  const handleRemove = (fileId: string) => {
    Modal.confirm({
      title: 'Supprimer le fichier',
      content: 'Êtes-vous sûr de vouloir supprimer ce fichier de la liste?',
      okText: 'Supprimer',
      cancelText: 'Annuler',
      okType: 'danger',
      onOk: () => {
        const newFileIds = fileIds.filter((id: string) => id !== fileId);
        // Always return as JSON string to maintain consistency
        onChange?.(JSON.stringify(newFileIds));
      }
    });
  };

  // Handle file upload
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${DIRECTUS_BASE_URL}/files`,
    headers: {
      Authorization: `Bearer ${getToken().token || ''}`
    },
    accept: '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.heic,.heif',
    showUploadList: false,
    customRequest: async (options) => {
      const { file, onSuccess, onError, onProgress } = options;
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await fetch(`${DIRECTUS_BASE_URL}/files`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken().token || ''}`
          },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          onSuccess?.(result);
        } else {
          throw new Error(`Upload failed: ${response.status}`);
        }
      } catch (error) {
        onError?.(error as Error);
      }
    },
    beforeUpload: (file) => {
      const maxSize = 35 * 1024 * 1024; // 35MB in bytes
      
      // Check file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
        'image/svg+xml',
        'image/heic',
        'image/heif'
      ];
      
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif'];
      
      const isValidType = allowedTypes.includes(file.type) && allowedExtensions.includes(fileExtension || '');
      
      if (!isValidType) {
        messageApi.error(`Le fichier "${file.name}" n'est pas un type autorisé. Seuls les fichiers PDF et images sont acceptés.`);
        return false;
      }
      
      if (file.size > maxSize) {
        messageApi.error(`Le fichier "${file.name}" est trop volumineux. Taille maximale autorisée: 35MB`);
        return false;
      }
      
      setUploading(true);
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        const newFileId = info.file.response?.data?.id;
        if (newFileId) {
          const newFileIds = [...fileIds, newFileId];
          // Always return as JSON string to maintain consistency
          onChange?.(JSON.stringify(newFileIds));
          messageApi.success(`${info.file.name} téléchargé avec succès`);
        }
        setUploading(false);
      } else if (info.file.status === 'error') {
        messageApi.error(`Erreur lors du téléchargement de ${info.file.name}`);
        setUploading(false);
      }
    },
    onRemove: () => {
      setUploading(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {contextHolder}
      
      {/* Upload section */}
      {!disabled && (
        <Space style={{ marginBottom: 16 }}>
          <Upload {...uploadProps}>
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
              disabled={uploading}
            >
              {uploading ? 'Téléchargement...' : 'Télécharger un fichier'}
            </Button>
          </Upload>
        </Space>
      )}

      {/* Files list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin size="large" />
          <div>Chargement des fichiers...</div>
        </div>
      ) : files.length > 0 ? (
        <List
          itemLayout="horizontal"
          dataSource={files}
          renderItem={(file) => (
            <List.Item
              actions={[
                <Button
                  key="view"
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => handleView(file)}
                  title="Voir le fichier"
                />,
                <Button
                  key="download"
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(file)}
                  title="Télécharger le fichier"
                />,
                !disabled && (
                  <Button
                    key="delete"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(file.id)}
                    title="Supprimer de la liste"
                  />
                )
              ].filter(Boolean)}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{file.filename_download}</Text>
                    <Text type="secondary">({formatFileSize(typeof file.filesize === 'number' ? file.filesize : parseInt(file.filesize) || 0)})</Text>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">Type: {file.type}</Text>
                    <Text type="secondary">Téléchargé le: {formatDate(file.uploaded_on)}</Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
          {disabled ? 'Aucun fichier' : 'Aucun fichier. Utilisez le bouton ci-dessus pour télécharger des fichiers.'}
        </div>
      )}

      {/* Summary */}
      {files.length > 0 && (
        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
          <Text type="secondary">
            {files.length} fichier{files.length > 1 ? 's' : ''} • {' '}
            Taille totale: {formatFileSize(files.reduce((total, file) => total + (typeof file.filesize === 'number' ? file.filesize : parseInt(file.filesize) || 0), 0))}
          </Text>
        </div>
      )}
    </div>
  );
};

export default MultiFileUpload;
